import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import TicketVuelta from './TicketVuelta';

const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Complementar = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [vuelta, setVuelta]             = useState(null);
    const [productos, setProductos]       = useState([]);
    const [empleados, setEmpleados]       = useState([]);
    const [catalogoProductos, setCatalogoProductos] = useState([]);
    const [statusGuardar, setStatusGuardar] = useState(false);
    const [sucursalNombre, setSucursalNombre] = useState('');
    const [mostrarTicket, setMostrarTicket]   = useState(false);

    const cargar = useCallback(async () => {
        const vueltaId = parseInt(id, 10);
        const [v, prods, emps] = await Promise.all([
            db.vueltas.get(vueltaId),
            db.vueltas_productos.where('vuelta_local_id').equals(vueltaId).toArray(),
            db.empleados.toArray(),
        ]);

        if (!v) { navigate('/vueltas'); return; }

        setVuelta(v);
        setEmpleados(emps);

        const sesion = await db.sesion.toArray();
        const sucursalId = sesion[0]?.sucursal?._id || sesion[0]?.sucursal;
        const sucReg = await db.sucursales
            .filter(s => String(s._id || s.id) === String(sucursalId))
            .first();
        setSucursalNombre(sucReg?.nombre || '');

        const catalogo = await db.productos.toArray();
        setCatalogoProductos(catalogo);
        setProductos(prods.map(p => {
            const prod = catalogo.find(c => c._id === p.producto || String(c.id) === String(p.producto));
            return { ...p, nombre: prod?.nombre || '—', cantidad_regresada: p.cantidad_regresada ?? 0 };
        }));
    }, [id, navigate]);

    useEffect(() => { cargar(); }, [cargar]);

    const resolverEmpleado = (empId) => {
        const emp = empleados.find(e => e._id === empId || String(e.id) === String(empId));
        if (!emp) return '—';
        return `${emp.nombre} ${emp.apellido_paterno || ''}`.trim();
    };

    const totalRecalculado = productos.reduce((acc, p) => {
        return acc + (p.precio * (p.cantidad - p.cantidad_regresada));
    }, 0);

    const handleCantidadRegresada = (productoId, valor) => {
        setProductos(prev => prev.map(p => {
            if (p.id !== productoId) return p;
            return { ...p, cantidad_regresada: Math.min(Math.max(0, valor), p.cantidad) };
        }));
    };

    const handleGuardar = async () => {
        setStatusGuardar(true);
        try {
            const empleadosActualizados = (vuelta?.empleados || []).map(ev => {
                let totalComision = 0;
                productos.forEach(p => {
                    const cantidadEfectiva = (p.cantidad || 0) - (p.cantidad_regresada || 0);
                    if (cantidadEfectiva <= 0) return;
                    const prodCatalogo = catalogoProductos.find(
                        c => c._id === p.producto || String(c.id) === String(p.producto)
                    );
                    const comisionPersonal = prodCatalogo?.comisiones_empleado?.find(c => c.empleado === ev.empleado);
                    const comisionTipo     = prodCatalogo?.comisiones?.find(c => c.tipo === ev.tipo);
                    const comision         = comisionPersonal ?? comisionTipo;
                    if (comision) totalComision += comision.cantidad * cantidadEfectiva;
                });
                return { ...ev, total_comision: totalComision };
            });

            await db.vueltas.update(parseInt(id, 10), {
                total:     totalRecalculado,
                empleados: empleadosActualizados,
            });
            for (const p of productos) {
                await db.vueltas_productos.update(p.id, { cantidad_regresada: p.cantidad_regresada });
            }

            dispatch(setAlert('Venta ambulante actualizada correctamente', 'success'));
            cargar();
        } catch {
            dispatch(setAlert('Error al guardar los cambios', 'danger'));
        } finally {
            setStatusGuardar(false);
        }
    };

    const handleImprimir = () => {
        const carritoTicket = productos.map(p => ({
            nombre:       p.nombre,
            cantidad:     p.cantidad - p.cantidad_regresada,
            precio_total: p.precio,
            extras:       [],
        })).filter(p => p.cantidad > 0);
        setMostrarTicket({
            folio:       vuelta.folio,
            fecha:       new Date(vuelta.fecha),
            vendedor:    resolverEmpleado(vuelta.vendedor),
            responsable: resolverEmpleado(vuelta.responsable),
            sucursal:    sucursalNombre,
            carrito:     carritoTicket,
            total:       totalRecalculado,
        });
    };

    if (!vuelta) {
        return (
            <div style={styles.loadingWrap}>
                <span className="spinner-border" style={{ color: '#F5C800' }} role="status" />
            </div>
        );
    }

    const fecha    = new Date(vuelta.fecha);
    const fechaStr = fecha.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const horaStr  = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

    return (
        <>
        {mostrarTicket && (
            <TicketVuelta data={mostrarTicket} onCerrar={() => setMostrarTicket(false)} />
        )}

        <div style={styles.page}>

            {/* ── Encabezado ── */}
            <div style={styles.topBar}>
                <button style={styles.btnVolver} onClick={() => navigate('/vueltas')}>
                    <i className="fas fa-arrow-left me-2"></i>
                    Ventas ambulantes
                </button>
                <div style={styles.topRight}>
                    <h4 style={styles.titulo}>
                        Complementar venta ambulante
                        <span style={styles.folioTag}>{vuelta.folio}</span>
                    </h4>
                    <span style={vuelta.status ? styles.badgeFinalizada : styles.badgePendiente}>
                        {vuelta.status ? 'Finalizada' : 'Pendiente'}
                    </span>
                </div>
                <button style={styles.btnImprimir} onClick={handleImprimir}>
                    <i className="fas fa-print me-2"></i>
                    Imprimir
                </button>
            </div>

            {/* ── Info general ── */}
            <div style={styles.infoGrid}>
                <InfoItem icon="fa-calendar"    label="Fecha"        value={`${fechaStr.charAt(0).toUpperCase() + fechaStr.slice(1)} · ${horaStr}`} />
                <InfoItem icon="fa-user"        label="Vendedor"     value={resolverEmpleado(vuelta.vendedor)} />
                <InfoItem icon="fa-user-shield" label="Responsable"  value={resolverEmpleado(vuelta.responsable)} />
                <InfoItem icon="fa-dollar-sign" label="Total"        value={`$${fmt(totalRecalculado)}`} color="#48BB78" />
            </div>

            {/* ── Productos ── */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <i className="fas fa-box me-2" style={{ color: '#F5C800' }}></i>
                    Productos
                </div>
                {productos.map((p, idx) => (
                    <div key={p.id} style={{
                        ...styles.prodFila,
                        borderBottom: idx < productos.length - 1 ? '1px solid #222' : 'none',
                    }}>
                        <div style={styles.prodInfo}>
                            <span style={styles.prodNombre}>{p.nombre}</span>
                            <span style={styles.prodSub}>
                                Llevados: <strong style={{ color: '#F0F0F0' }}>{p.cantidad}</strong>
                                {'  ·  '}
                                Vendidos: <strong style={{ color: '#48BB78' }}>{p.cantidad - p.cantidad_regresada}</strong>
                            </span>
                        </div>
                        <div style={styles.prodDerecha}>
                            <span style={styles.regresadosLabel}>Regresados</span>
                            <div style={styles.cantidadControl}>
                                <button
                                    style={styles.btnQty}
                                    onClick={() => handleCantidadRegresada(p.id, p.cantidad_regresada - 1)}
                                    disabled={p.cantidad_regresada <= 0}
                                >−</button>
                                <span style={styles.cantidadNum}>{p.cantidad_regresada}</span>
                                <button
                                    style={styles.btnQty}
                                    onClick={() => handleCantidadRegresada(p.id, p.cantidad_regresada + 1)}
                                    disabled={p.cantidad_regresada >= p.cantidad}
                                >+</button>
                            </div>
                        </div>
                    </div>
                ))}
                {productos.length === 0 && (
                    <p style={{ color: '#555', padding: '1.5rem', margin: 0, textAlign: 'center' }}>Sin productos</p>
                )}
                <div style={styles.totalRow}>
                    <span style={{ color: '#888', fontSize: '0.9rem' }}>Total</span>
                    <span style={styles.totalValor}>${fmt(totalRecalculado)}</span>
                </div>
            </div>

            {/* ── Guardar ── */}
            {!vuelta.status && (
                <button
                    style={statusGuardar ? { ...styles.btnGuardar, opacity: 0.6, cursor: 'not-allowed' } : styles.btnGuardar}
                    onClick={handleGuardar}
                    disabled={statusGuardar}
                >
                    {statusGuardar ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status" />Guardando...</>
                    ) : (
                        <><i className="fas fa-check me-2"></i>Guardar</>
                    )}
                </button>
            )}
        </div>
        </>
    );
};

const InfoItem = ({ icon, label, value, color = '#F5C800' }) => (
    <div style={styles.infoItem}>
        <div style={{ ...styles.infoIconWrap, background: `${color}14`, border: `1px solid ${color}33` }}>
            <i className={`fas ${icon}`} style={{ color, fontSize: '0.95rem' }}></i>
        </div>
        <div>
            <div style={styles.infoLabel}>{label}</div>
            <div style={styles.infoValue}>{value}</div>
        </div>
    </div>
);

const styles = {
    page: {
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
    },
    loadingWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)',
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    btnVolver: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.5rem 1rem',
        color: '#888',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        flexShrink: 0,
    },
    topRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
        flex: 1,
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    folioTag: {
        background: 'rgba(245,200,0,0.12)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '8px',
        padding: '0.15rem 0.65rem',
        fontSize: '0.85rem',
        fontFamily: 'monospace',
        fontWeight: 700,
    },
    badgeFinalizada: {
        background: 'rgba(245,200,0,0.1)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.7rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    badgePendiente: {
        background: 'rgba(237,137,54,0.12)',
        color: '#ED8936',
        border: '1px solid rgba(237,137,54,0.35)',
        borderRadius: '20px',
        padding: '0.2rem 0.7rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0.75rem',
    },
    infoItem: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '12px',
        padding: '1rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
    },
    infoIconWrap: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    infoLabel: {
        color: '#666',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '0.2rem',
    },
    infoValue: {
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '0.92rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    cardHeader: {
        background: '#111',
        borderBottom: '1px solid #2E2E2E',
        padding: '0.85rem 1.25rem',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
    },
    prodFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1.25rem',
        gap: '1rem',
    },
    prodInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
        flex: 1,
    },
    prodNombre: {
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    prodSub: {
        color: '#555',
        fontSize: '0.8rem',
    },
    prodDerecha: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '0.25rem',
        flexShrink: 0,
    },
    regresadosLabel: {
        color: '#555',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    cantidadControl: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
    },
    btnQty: {
        width: '30px',
        height: '30px',
        background: '#222',
        border: '1px solid #333',
        borderRadius: '7px',
        color: '#F0F0F0',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
    },
    cantidadNum: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        minWidth: '24px',
        textAlign: 'center',
    },
    totalRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.85rem 1.25rem',
        background: 'rgba(245,200,0,0.04)',
        borderTop: '1px solid #2E2E2E',
    },
    totalValor: {
        color: '#F5C800',
        fontWeight: 800,
        fontSize: '1.2rem',
    },
    btnImprimir: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.5)',
        borderRadius: '10px',
        padding: '0.5rem 1rem',
        color: '#F5C800',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        flexShrink: 0,
        marginLeft: 'auto',
    },
    btnGuardar: {
        background: '#F5C800',
        color: '#111',
        border: 'none',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
    },
};

export default Complementar;
